import { useState, useEffect, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Wallet, ArrowRight, RefreshCw, Loader2, ArrowRightLeft, X } from 'lucide-react';

const WALLETS = [
  { key: 'fund', label: 'Fund Wallet', icon: '💰', field: 'fund_balance', desc: 'Main deposit/withdraw' },
  { key: 'spot', label: 'Spot Wallet', icon: '📈', field: 'spot_balance', desc: 'For spot trading' },
  { key: 'futures', label: 'Futures Wallet', icon: '⚡', field: 'futures_balance', desc: 'For futures margin' },
];

/**
 * Wallet Transfer widget — moves USDT between fund / spot / futures.
 * Can be rendered inline OR triggered as a dialog via the `triggerLabel` prop.
 */
export function WalletTransfer({ dark = false, onDone, autoOpen = false, triggerLabel = 'Transfer' }) {
  const [open, setOpen] = useState(autoOpen);
  const [balances, setBalances] = useState({ fund_balance: 0, spot_balance: 0, futures_balance: 0, currency: 'USDT' });
  const [from, setFrom] = useState('fund');
  const [to, setTo] = useState('spot');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get('/wallet/balances');
      setBalances(data);
    } catch (err) { console.error('[Wallet] balances load:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const swap = () => { const t = from; setFrom(to); setTo(t); };
  const srcBal = balances[`${from}_balance`] || 0;
  const amtNum = parseFloat(amount) || 0;
  const canSubmit = amtNum > 0 && amtNum <= srcBal && from !== to;

  const handleTransfer = async () => {
    if (!canSubmit) { toast.error('Invalid transfer'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/wallet/transfer', { from_wallet: from, to_wallet: to, amount: amtNum });
      toast.success(`Transferred $${amtNum} · ${from.toUpperCase()} → ${to.toUpperCase()}`);
      setBalances(prev => ({
        ...prev,
        fund_balance: data.new_fund_balance,
        spot_balance: data.new_spot_balance,
        futures_balance: data.new_futures_balance,
      }));
      setAmount('');
      onDone?.(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const themeCls = dark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-brand-border text-brand-text-primary';
  const bgCls = dark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-brand-bg border-brand-border hover:bg-brand-bg/70';
  const subTextCls = dark ? 'text-slate-400' : 'text-brand-text-secondary';

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className={dark ? "h-8 border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-700 text-xs rounded-lg" : "h-8 rounded-lg text-xs border-brand-border"}
        data-testid="wallet-transfer-btn"
      >
        <ArrowRightLeft size={12} className="mr-1.5" /> {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`sm:max-w-lg ${themeCls}`} data-testid="wallet-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wallet size={16} className="text-brand-primary" /> Transfer Between Wallets
            </DialogTitle>
            <p className={`text-xs ${subTextCls}`}>Move USDT between Fund, Spot, and Futures wallets instantly. No fees.</p>
          </DialogHeader>

          {/* Balance overview */}
          <div className="grid grid-cols-3 gap-2 my-2">
            {WALLETS.map(w => (
              <div key={w.key} className={`p-2.5 rounded-lg border ${bgCls}`} data-testid={`balance-${w.key}`}>
                <div className="text-xs">{w.icon} <span className="font-semibold">{w.label.replace(' Wallet', '')}</span></div>
                <div className={`font-mono text-sm font-bold mt-1 ${dark ? 'text-slate-100' : 'text-brand-text-primary'}`}>
                  {loading ? <Loader2 size={12} className="animate-spin" /> : `$${(balances[w.field] || 0).toFixed(2)}`}
                </div>
              </div>
            ))}
          </div>

          {/* From/To selectors */}
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
              <div>
                <label className={`text-[10px] uppercase tracking-wider font-semibold ${subTextCls}`}>From</label>
                <select value={from} onChange={e => setFrom(e.target.value)}
                  className={`w-full h-10 px-3 rounded-lg border text-sm font-semibold ${dark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-brand-border text-brand-text-primary'}`}
                  data-testid="transfer-from-select">
                  {WALLETS.map(w => <option key={w.key} value={w.key}>{w.icon} {w.label}</option>)}
                </select>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={swap} className="h-10 w-10 rounded-lg mt-4" data-testid="swap-btn">
                <ArrowRight size={14} />
              </Button>
              <div>
                <label className={`text-[10px] uppercase tracking-wider font-semibold ${subTextCls}`}>To</label>
                <select value={to} onChange={e => setTo(e.target.value)}
                  className={`w-full h-10 px-3 rounded-lg border text-sm font-semibold ${dark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-brand-border text-brand-text-primary'}`}
                  data-testid="transfer-to-select">
                  {WALLETS.filter(w => w.key !== from).map(w => <option key={w.key} value={w.key}>{w.icon} {w.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={`text-[10px] uppercase tracking-wider font-semibold flex justify-between mb-1 ${subTextCls}`}>
                <span>Amount (USDT)</span>
                <span>Available: ${srcBal.toFixed(2)}</span>
              </label>
              <Input
                type="number"
                step="any"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                className={`font-mono ${dark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-brand-border'}`}
                data-testid="transfer-amount-input"
              />
              <div className="grid grid-cols-4 gap-1 mt-2">
                {[25, 50, 75, 100].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setAmount((srcBal * p / 100).toFixed(2))}
                    className={`text-[10px] py-1 rounded font-bold ${dark ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-brand-bg hover:bg-brand-bg/70 text-brand-text-secondary'}`}
                    data-testid={`transfer-pct-${p}`}
                  >{p}%</button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={reload} className={dark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : ""} data-testid="wallet-refresh-btn">
              <RefreshCw size={12} className="mr-1" /> Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}><X size={12} className="mr-1" /> Close</Button>
            <Button
              size="sm"
              onClick={handleTransfer}
              disabled={!canSubmit || busy}
              className="bg-brand-primary hover:bg-brand-primary-hover text-white"
              data-testid="confirm-transfer-btn"
            >
              {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <ArrowRightLeft size={12} className="mr-1" />}
              Transfer ${amtNum.toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
